import * as THREE from 'three';
import { LightProbeGenerator } from 'three/addons/lights/LightProbeGenerator.js';
import type { LightingProfile, ReflectionProbe } from './lighting-profile';
import type { GraphicsSettings } from './graphics-settings';
import { probeLightingKey } from './probe-keys';
import { patchProbeShader, probeUniforms } from './probe-shader';

interface Capture {id:string;cube:THREE.WebGLCubeRenderTarget;camera:THREE.CubeCamera;face:number}
interface Cached {target:THREE.WebGLRenderTarget;used:number}
const vector=(p:{x:number;y:number;z:number})=>new THREE.Vector3(p.x,p.y,p.z);

/** Bounded, static-room captures. Dynamic avatars/editor helpers never enter a probe. */
export class ReflectionProbes {
  private profile: LightingProfile | null = null;
  private lightingKey='';
  private geometryKey='';
  private cache=new Map<string,Cached>();
  private job:Capture|null=null;
  private uniforms=probeUniforms();
  private bound=new Set<THREE.MeshStandardMaterial>();
  private originals=new WeakMap<THREE.MeshStandardMaterial,{compile:THREE.MeshStandardMaterial['onBeforeCompile'];key:THREE.MeshStandardMaterial['customProgramCacheKey']}>();
  private placeholder=new THREE.DataTexture(new Uint8Array([0,0,0,255]),1,1);
  private pmrem:THREE.PMREMGenerator;
  private quality:Pick<GraphicsSettings,'reflections'|'indirectLighting'>={reflections:'low',indirectLighting:false};
  private selected:ReflectionProbe[]=[];
  private quietUntil=0;
  private captures=0;
  private baking=false;
  private disposed=false;
  private error='';
  constructor(private scene:THREE.Scene,private renderer:THREE.WebGLRenderer,private hdr:boolean) {
    this.placeholder.needsUpdate=true;this.uniforms.owMap0.value=this.uniforms.owMap1.value=this.placeholder;
    this.pmrem=new THREE.PMREMGenerator(renderer);
  }
  setProfile(profile:LightingProfile):void {
    const key=probeLightingKey(profile);this.profile=profile;
    if(key!==this.lightingKey){this.lightingKey=key;this.invalidate();}
  }
  setQuality(quality:GraphicsSettings):void {
    const next={reflections:quality.reflections,indirectLighting:quality.indirectLighting};
    if(next.reflections!==this.quality.reflections||next.indirectLighting!==this.quality.indirectLighting){this.quality=next;this.invalidate();}
  }
  setGeometryKey(key:string):void {if(key!==this.geometryKey){this.geometryKey=key;this.invalidate();}}
  private get size():number {return this.quality.reflections==='high'?256:this.quality.reflections==='medium'?128:64;}
  private get limit():number {return this.quality.reflections==='medium'?1:2;}
  private validBake(probe:ReflectionProbe):boolean {return !!probe.baked&&probe.baked.lightingKey===this.lightingKey&&probe.baked.geometryKey===this.geometryKey;}
  private needsCapture(probe:ReflectionProbe):boolean {return this.quality.reflections!=='low'||this.quality.indirectLighting&&!this.validBake(probe);}
  private invalidate():void {
    this.job?.cube.dispose();this.job=null;
    for(const entry of this.cache.values())entry.target.dispose();this.cache.clear();
    this.pmrem.dispose();if(!this.disposed)this.pmrem=new THREE.PMREMGenerator(this.renderer);
    this.quietUntil=performance.now()+350;this.error='';this.clearUniforms();
  }
  private clearUniforms():void {
    this.uniforms.owReflection.value.set(0,0);this.uniforms.owIndirect.value.set(0,0);
    this.uniforms.owMap0.value=this.uniforms.owMap1.value=this.placeholder;
  }
  private bindMaterials():void {
    this.scene.traverse(node=>{
      if(!(node instanceof THREE.Mesh))return;
      for(const material of Array.isArray(node.material)?node.material:[node.material]) {
        if(!(material instanceof THREE.MeshStandardMaterial)||this.bound.has(material))continue;
        const compile=material.onBeforeCompile,key=material.customProgramCacheKey,previousKey=key.call(material);
        this.originals.set(material,{compile,key});this.bound.add(material);
        material.onBeforeCompile=(shader,renderer)=>{compile.call(material,shader,renderer);patchProbeShader(shader,this.uniforms);};
        material.customProgramCacheKey=()=>`${previousKey}:offworld-probes-v2`;material.needsUpdate=true;
        material.addEventListener('dispose',()=>this.bound.delete(material));
      }
    });
  }
  private createCapture(probe:ReflectionProbe,size=this.size):Capture {
    const cube=new THREE.WebGLCubeRenderTarget(size,{type:THREE.HalfFloatType,generateMipmaps:false,minFilter:THREE.LinearFilter});
    cube.texture.colorSpace=THREE.LinearSRGBColorSpace;
    const camera=new THREE.CubeCamera(.1,256,cube);camera.position.copy(vector(probe.position));
    camera.coordinateSystem=THREE.WebGLCoordinateSystem;camera.updateCoordinateSystem();camera.updateMatrixWorld(true);
    return {id:probe.id,cube,camera,face:0};
  }
  private capture<T>(roots:readonly THREE.Object3D[],work:()=>T,diffuseOnly=false):T {
    const allowed=new Set(roots),hidden:THREE.Object3D[]=[];
    const skyUniforms:{uniform:THREE.IUniform;value:number}[]=[];
    for(const node of this.scene.children) if('isSky' in node&&node instanceof THREE.Mesh&&node.material instanceof THREE.ShaderMaterial) {
      const uniform=node.material.uniforms.roomSkyIntensity;
      if(uniform){skyUniforms.push({uniform,value:uniform.value as number});uniform.value=this.profile?.sky.intensity??1;}
    }
    for(const node of this.scene.children) if(node.visible&&((diffuseOnly&&'isSky' in node)||(!allowed.has(node)&&!(node instanceof THREE.Light)&&!('isSky' in node)))){hidden.push(node);node.visible=false;}
    const background=this.scene.background;if(diffuseOnly)this.scene.background=new THREE.Color(0);
    const fog=this.scene.fog,target=this.renderer.getRenderTarget(),face=this.renderer.getActiveCubeFace(),mip=this.renderer.getActiveMipmapLevel();
    const auto=this.renderer.autoClear,xr=this.renderer.xr.enabled;
    this.scene.fog=null;this.renderer.autoClear=true;this.renderer.xr.enabled=false;this.uniforms.owCapture.value=true;
    try{return work();}finally {
      this.uniforms.owCapture.value=false;this.scene.fog=fog;hidden.forEach(node=>node.visible=true);
      this.scene.background=background;
      skyUniforms.forEach(({uniform,value})=>uniform.value=value);
      this.renderer.autoClear=auto;this.renderer.xr.enabled=xr;this.renderer.setRenderTarget(target,face,mip);
    }
  }
  update(camera:THREE.Camera,roots:readonly THREE.Object3D[],ready:boolean):void {
    if(this.disposed)return;
    if(!this.hdr||!this.profile?.probes?.length||(this.quality.reflections==='low'&&!this.quality.indirectLighting)){this.selected=[];this.clearUniforms();return;}
    this.bindMaterials();
    const position=camera.getWorldPosition(new THREE.Vector3());
    this.selected=[...this.profile.probes].sort((a,b)=>new THREE.Box3(vector(a.bounds.min),vector(a.bounds.max)).distanceToPoint(position)-new THREE.Box3(vector(b.bounds.min),vector(b.bounds.max)).distanceToPoint(position)||a.id.localeCompare(b.id)).slice(0,this.limit);
    if(!this.baking&&!this.error&&ready&&performance.now()>=this.quietUntil) {
      if(this.job&&!this.selected.some(p=>p.id===this.job!.id)){this.job.cube.dispose();this.job=null;}
      const next=this.selected.find(p=>this.needsCapture(p)&&!this.cache.has(p.id));
      if(!this.job&&next)this.job=this.createCapture(next);
      if(this.job) {
        try {
          const job=this.job;
          if(job.face<6) {
            this.capture(roots,()=>{this.renderer.setRenderTarget(job.cube,job.face);this.renderer.render(this.scene,job.camera.children[job.face] as THREE.Camera);});job.face++;
          } else {
            const target=this.pmrem.fromCubemap(job.cube.texture);
            this.cache.set(job.id,{target,used:performance.now()});this.captures++;job.cube.dispose();this.job=null;
          }
        } catch(error){this.error=String(error);this.job?.cube.dispose();this.job=null;}
      }
    }
    for(const probe of this.selected){const cached=this.cache.get(probe.id);if(cached)cached.used=performance.now();}
    while(this.cache.size>this.limit) {
      const victim=[...this.cache].filter(([id])=>!this.selected.some(p=>p.id===id)).sort((a,b)=>a[1].used-b[1].used)[0];
      if(!victim)break;victim[1].target.dispose();this.cache.delete(victim[0]);
    }
    this.applyUniforms();
  }
  private applyUniforms():void {
    this.clearUniforms();
    for(let i=0;i<this.selected.length;i++) {
      const probe=this.selected[i]!,cached=this.cache.get(probe.id),baked=this.validBake(probe)?probe.baked:undefined;
      this.uniforms.owPositions.value[i]!.copy(vector(probe.position));this.uniforms.owMin.value[i]!.copy(vector(probe.bounds.min));this.uniforms.owMax.value[i]!.copy(vector(probe.bounds.max));
      this.uniforms.owBlend.value.setComponent(i,probe.blend);this.uniforms.owIntensity.value.setComponent(i,probe.intensity);
      if(cached) {
        const map=cached.target.texture;
        (i?this.uniforms.owMap1:this.uniforms.owMap0).value=map;
        this.uniforms.owAtlas.value.set(1/cached.target.width,1/cached.target.height,Math.log2(cached.target.height/4));
        this.uniforms.owReflection.value.setComponent(i,this.quality.reflections==='low'?0:1);
      }
      if(this.quality.indirectLighting) {
        this.uniforms.owIndirect.value.setComponent(i,baked?2:cached?1:0);
        if(baked) (i?this.uniforms.owSH1:this.uniforms.owSH0).value.forEach((v,index)=>v.fromArray(baked.coefficients,index*3));
      }
    }
  }
  /** Offline authoring operation. Captures all probes and exports linear SH irradiance data. */
  async bake(roots:readonly THREE.Object3D[]):Promise<ReflectionProbe[]> {
    if(!this.hdr||this.baking||this.disposed)throw new Error('HDR probes are unavailable or a bake is already running');
    const profile=this.profile,lightingKey=this.lightingKey,geometryKey=this.geometryKey;
    if(!profile?.probes?.length)throw new Error('The room has no authored probe volumes');
    this.baking=true;this.bindMaterials();const output:ReflectionProbe[]=[];
    try {
      for(const probe of profile.probes) {
        const capture=this.createCapture(probe,64);
        try {
          // Bake outgoing light from static surfaces only. Direct sky irradiance
          // is already part of the room's lighting, so exclude it from the bounce.
          this.capture(roots,()=>capture.camera.update(this.renderer,this.scene),true);
          const light=await LightProbeGenerator.fromCubeRenderTarget(this.renderer,capture.cube);
          if(this.disposed||lightingKey!==this.lightingKey||geometryKey!==this.geometryKey)throw new Error('Room changed while baking probes');
          const coefficients=light.sh.coefficients.flatMap(v=>v.toArray().map(n=>Number(n.toFixed(5))));
          if(coefficients.some(n=>!Number.isFinite(n)||Math.abs(n)>256))throw new Error('Non-finite or out-of-range probe radiance');
          output.push({...structuredClone(probe),baked:{lightingKey,geometryKey,coefficients}});
        } finally {capture.cube.dispose();}
      }
      return output;
    } finally {this.baking=false;}
  }
  get pending():boolean {return this.baking||!!this.job||this.selected.some(p=>this.needsCapture(p)&&!this.cache.has(p.id))&&!this.error;}
  get diagnostics(){return {authored:this.profile?.probes?.length??0,active:this.selected.map(p=>p.id),resident:this.cache.size,pending:this.pending,face:this.job?.face??null,captures:this.captures,resolution:this.size,baked:this.selected.filter(p=>this.validBake(p)).length,geometryKey:this.geometryKey,lightingKey:this.lightingKey,error:this.error};}
  dispose():void {
    this.disposed=true;this.invalidate();this.placeholder.dispose();
    for(const material of this.bound){const original=this.originals.get(material)!;material.onBeforeCompile=original.compile;material.customProgramCacheKey=original.key;material.needsUpdate=true;}
    this.bound.clear();
  }
}
